import { Polyline, Popup } from 'react-leaflet'
import L from 'leaflet'
import { memo } from 'react'
import type { LegAssignment, LegLog, Runner } from '../../types/index'
import { formatDuration } from '../../utils/formatTime'
import { formatPace } from '../../utils/paceCalc'

// Canvas renderer is far faster than SVG for large numbers of path points
const canvasRenderer = L.canvas({ padding: 0.5 })

interface RouteOverlayProps {
  legs: LegAssignment[]
  logs?: LegLog[]
  runners?: Runner[]
  currentLegNumber: number
}

const PT = { timeZone: 'America/Los_Angeles' }
function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('en-US', { ...PT, hour: '2-digit', minute: '2-digit' })
}

export const RouteOverlay = memo(function RouteOverlay({
  legs,
  logs = [],
  runners = [],
  currentLegNumber,
}: RouteOverlayProps) {
  const logByLegNum = new Map(logs.map(l => [l.legNumber, l]))
  const runnerById = new Map(runners.map(r => [r.id, r]))

  // Compute expected chain start times from leg 1's actual start
  const sortedLegs = [...legs].sort((a, b) => a.legNumber - b.legNumber)
  const leg1Log = logByLegNum.get(1)
  const chainStartTimes = new Map<number, number>()
  if (leg1Log?.startTime) {
    let t = new Date(leg1Log.startTime).getTime()
    for (const leg of sortedLegs) {
      chainStartTimes.set(leg.legNumber, t)
      const runner = leg.runnerId ? runnerById.get(leg.runnerId) : null
      const pace = leg.estimatedPacePerMile ?? runner?.expectedPace ?? 10
      t += (leg.distanceMiles ?? 0) * pace * 60 * 1000
    }
  }

  return (
    <>
      {legs.map(leg => {
        const isCompleted = leg.legNumber < currentLegNumber
        const isCurrent = leg.legNumber === currentLegNumber
        const isPending = !isCompleted && !isCurrent

        let color = '#9ca3af'
        let weight = 3
        let opacity = 0.5

        if (isCompleted) {
          color = '#22c55e'
          weight = 4
          opacity = 0.8
        } else if (isCurrent) {
          color = '#ef4444'
          weight = 5
          opacity = 0.9
        }

        const positions: L.LatLngTuple[] =
          leg.routePoints && leg.routePoints.length > 1
            ? (leg.routePoints as L.LatLngTuple[])
            : [[leg.startLat, leg.startLng], [leg.endLat, leg.endLng]]

        const log = logByLegNum.get(leg.legNumber)
        const runner = leg.runnerId ? runnerById.get(leg.runnerId) : null
        const pace = leg.estimatedPacePerMile ?? runner?.expectedPace ?? 10
        const estDurationSec = leg.distanceMiles ? Math.floor(leg.distanceMiles * pace * 60) : null
        const estStart = chainStartTimes.get(leg.legNumber)
        const estEnd = estStart && estDurationSec ? estStart + estDurationSec * 1000 : null
        const done = !!(log?.endTime)

        const status = isCompleted ? 'c' : isCurrent ? 'a' : 'p'
        return (
          <Polyline
            key={`${leg.id}-${status}`}
            positions={positions}
            color={color}
            weight={weight}
            opacity={opacity}
            dashArray={isPending ? '5, 5' : undefined}
            renderer={canvasRenderer}
          >
            <Popup minWidth={180}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                <span style={{ fontWeight: 700, fontSize: 12 }}>Leg {leg.legNumber}</span>
                {done && <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>✓ Done</span>}
                {isCurrent && !done && <span style={{ fontSize: 10, color: '#dc2626', fontWeight: 600 }}>▶ Running</span>}
              </div>

              {/* Runner */}
              {runner && (
                <div style={{ fontSize: 11, color: '#374151', marginBottom: 4 }}>
                  {runner.name} · Van {runner.vanNumber}
                </div>
              )}

              {/* Leg stats row */}
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                {leg.distanceMiles?.toFixed(1)} mi
                {leg.difficultyRating ? ` · ${leg.difficultyRating}` : ''}
                {leg.elevationGain ? ` · +${leg.elevationGain}ft` : ''}
              </div>

              {/* Times */}
              {done && log ? (
                <div style={{ fontSize: 11, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px 8px', color: '#374151' }}>
                  <span style={{ color: '#6b7280' }}>Start</span><span style={{ fontWeight: 600 }}>{fmtTime(log.startTime)}</span>
                  <span style={{ color: '#6b7280' }}>End</span><span style={{ fontWeight: 600 }}>{fmtTime(log.endTime)}</span>
                  <span style={{ color: '#6b7280' }}>Time</span><span style={{ fontWeight: 600 }}>{log.actualDurationSeconds ? formatDuration(log.actualDurationSeconds) : '—'}</span>
                  <span style={{ color: '#6b7280' }}>Pace</span><span style={{ fontWeight: 600 }}>{log.actualPacePerMile ? formatPace(log.actualPacePerMile) : '—'}</span>
                </div>
              ) : (
                <div style={{ fontSize: 11, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px 8px', color: '#374151' }}>
                  {estStart && <><span style={{ color: '#6b7280' }}>Est start</span><span style={{ fontWeight: 600 }}>{fmtTime(new Date(estStart).toISOString())}</span></>}
                  {estEnd && <><span style={{ color: '#6b7280' }}>Est end</span><span style={{ fontWeight: 600 }}>{fmtTime(new Date(estEnd).toISOString())}</span></>}
                  {estDurationSec && <><span style={{ color: '#6b7280' }}>Est time</span><span style={{ fontWeight: 600 }}>{formatDuration(estDurationSec)}</span></>}
                  <span style={{ color: '#6b7280' }}>Est pace</span><span style={{ fontWeight: 600 }}>{formatPace(pace)}</span>
                </div>
              )}
            </Popup>
          </Polyline>
        )
      })}
    </>
  )
})
